import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import categoryService from '../../services/categoryService';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

const Collections = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all categories from backend
        const categoriesResponse = await categoryService.getCategories();

        if (categoriesResponse.success) {
          // Fetch first product for each category to get thumbnail image
          const collectionsPromises = categoriesResponse.categories.map(async (cat) => {
            try {
              // Fetch products for this category
              const productsResponse = await categoryService.getCategoryProducts(cat._id);

              // Get first product's first image, or use fallback
              let image = 'https://images.pexels.com/photos/1926769/pexels-photo-1926769.jpeg?auto=compress&cs=tinysrgb&w=800';

              if (productsResponse.success && productsResponse.products && productsResponse.products.length > 0) {
                const firstProduct = productsResponse.products[0];
                if (firstProduct.images && firstProduct.images.length > 0 && firstProduct.images[0].path) {
                  image = firstProduct.images[0].path;
                }
              }

              return {
                id: cat._id,
                name: cat.name || cat.category,
                image: image,
                link: `/category/${cat._id}`
              };
            } catch (err) {
              // If fetching products fails, use fallback image
              console.error(`Error fetching products for category ${cat._id}:`, err);
              return {
                id: cat._id,
                name: cat.name || cat.category,
                image: 'https://images.pexels.com/photos/1926769/pexels-photo-1926769.jpeg?auto=compress&cs=tinysrgb&w=800',
                link: `/category/${cat._id}`
              };
            }
          });

          // Wait for all category product fetches to complete
          const collections = await Promise.all(collectionsPromises);
          setCategories(collections);
        } else {
          setError('Failed to load collections');
        }
      } catch (err) {
        setError('Error loading collections');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Skeleton loader component
  const CollectionSkeleton = () => (
    <div className="relative rounded-lg overflow-hidden animate-pulse">
      <div className="w-full h-96 bg-gray-200"></div>
      <div className="absolute bottom-4 left-4">
        <div className="h-10 w-32 bg-gray-300 rounded"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <main className="flex-grow py-16 px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Title */}
          <h1 className="text-4xl font-normal text-center mb-16">Collections</h1>

          {/* Error Message */}
          {error && (
            <div className="max-w-md mx-auto mb-8 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-center">
              {error}
            </div>
          )}

          {/* Collections Grid */}
          {loading ? (
            // Skeleton loading - dynamic number based on expected categories
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <CollectionSkeleton />
              <CollectionSkeleton />
              <CollectionSkeleton />
              <CollectionSkeleton />
              <CollectionSkeleton />
              <CollectionSkeleton />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <p className="text-xl">No collections available.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {categories.map((collection) => (
                <Link
                  key={collection.id}
                  to={collection.link}
                  className="relative rounded-lg overflow-hidden group"
                >
                  <img
                    src={collection.image}
                    alt={collection.name}
                    className="w-full h-96 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute bottom-4 left-4">
                    <div className="bg-white px-6 py-2.5 rounded shadow-sm hover:shadow-md transition-shadow">
                      <span className="text-sm font-medium text-gray-900">
                        {collection.name}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Collections;
